import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { theme } from '@/styles/theme';

/**
 * Props para o componente PageBase.
 *
 * @param {string} title - O título da página.
 * @param {() => void} [onBackClick] - Função chamada ao clicar no botão de voltar (opcional).
 * @param {React.ReactNode} [children] - Conteúdo da página (opcional).
 */
interface PageBaseProps {
  title: string;
  onBackClick?: () => void;
  children?: React.ReactNode;
  paddingInline?: number;
  sxContainer?: any;
  sxContentBox?: any;
  sxHeader?: any;
}
/**
 * Componente PageBase.
 *
 * Um contêiner centralizado na página com um cabeçalho opcional contendo um botão de voltar
 * e um título. Adapta-se ao conteúdo passado como `children`.
 *
 * @param {PageBaseProps} props - Propriedades do componente.
 * @returns {JSX.Element} Um layout centralizado com um título e área de conteúdo.
 *
 * @example
 * <PageBase title="Cadastro" onBackClick={() => console.log('Voltar clicado')}>
 *   <form>Conteúdo do formulário aqui</form>
 * </PageBase>
 */
export const PageBase: React.FC<PageBaseProps> = ({
  title,
  onBackClick,
  paddingInline = 14,
  children,
  sxContainer,
  sxContentBox,
  sxHeader,
}) => {
  return (
    <Box sx={sxContainer || styles.container}>
      <Box sx={sxContentBox || styles.contentBox}>
        <Box sx={sxHeader || styles.header}>
          {onBackClick && (
            <IconButton
              onClick={onBackClick}
              sx={{ marginRight: 1 }}
              aria-label="Voltar"
            >
              <ArrowBackIosIcon color="primary" />
            </IconButton>
          )}
          <Typography variant="body2" component="h1">
            {title}
          </Typography>
        </Box>
        <Box sx={{ width: '100%', paddingInline: paddingInline }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
  },
  contentBox: {
    backgroundColor: theme.palette.secondary.contrastText,
    borderRadius: 2,
    width: 911,
    boxShadow: 1,
    paddingInline: 4,
    marginTop: 4,
    paddingBlock: 4,
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 2,
  },
};
